import { useState } from 'react';
import { X } from 'lucide-react';
import Button from '@/components/common/Button';
import { useCreateEvent } from '@/hooks/useEvents';

export default function EventCreateDialog({ onClose, sourceEmailId = null }) {
  const [formData, setFormData] = useState({
    title: '',
    event_date: '',
    event_type: 'reminder',
    description: '',
    source_email_id: sourceEmailId
  });

  const createEvent = useCreateEvent();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.event_date) {
      return;
    }

    try {
      await createEvent.mutateAsync(formData);
      onClose();
    } catch (err) {
      console.error('Failed to create event:', err);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">Create Event</h2>
          <Button variant="ghost" size="sm" onClick={onClose} className="p-1">
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="Event title"
              className="w-full px-3 py-2 text-sm rounded-md border bg-background"
              required
            />
          </div>

          {/* Date & Time */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Date & Time <span className="text-red-500">*</span>
            </label>
            <input
              type="datetime-local"
              value={formData.event_date}
              onChange={(e) => handleChange('event_date', e.target.value)}
              className="w-full px-3 py-2 text-sm rounded-md border bg-background"
              required
            />
          </div>

          {/* Type */}
          <div>
            <label className="block text-sm font-medium mb-1">Type</label>
            <select
              value={formData.event_type}
              onChange={(e) => handleChange('event_type', e.target.value)}
              className="w-full px-3 py-2 text-sm rounded-md border bg-background"
            >
              <option value="deadline">Deadline</option>
              <option value="appointment">Appointment</option>
              <option value="sale_end">Sale End</option>
              <option value="reminder">Reminder</option>
            </select>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Optional description"
              rows={3}
              className="w-full px-3 py-2 text-sm rounded-md border bg-background resize-none"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-2 justify-end pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!formData.title || !formData.event_date || createEvent.isPending}
            >
              {createEvent.isPending ? 'Creating...' : 'Create Event'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
